/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { DataValueFieldFactory } from "abreact/form/DataValueFieldFactory";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbReactContext } from "abreact/incubator/context/AbReactContext";
import { EditableItemModel } from "abreact/incubator/ui/managed_editing/WithEditableItemModel";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { useForceUpdate, useOnMount, useUniqueId } from "abreact/utils/AbHooks";
import * as MobXReactUtils from "abreact/utils/MobXReactUtils";
import * as React from "react";
export function useInputId(componentName, props) {
  const inputId = useUniqueId(componentName);
  return props.id ? `${props.id}-input` : inputId;
}
export function useEditableItemViewModel(props) {
  const context = React.useContext(AbReactContext);
  const editManager = props.editManager !== undefined ? props.editManager : context.editManager;
  // Track the state of the edit manager here so that the component can re-render when it changes
  const [isManagerEditing, setIsManagerEditing] = React.useState(editManager ? editManager.currentlyEditing : true);
  const editableItemModelRef = React.useRef(undefined);
  if (editableItemModelRef.current) {
    editableItemModelRef.current.setEditable(props.isEditable);
  }
  React.useEffect(() => {
    if (editManager) {
      const editableItem = props.createEditableItem();
      const editableItemModel = new EditableItemModel(editManager);
      editableItemModelRef.current = editableItemModel;
      editableItemModel.setCallbacks(editableItem);
      editableItemModel.setEditable(props.isEditable);
      editableItemModel.onStateChange = () => {
        setIsManagerEditing(editManager ? editManager.currentlyEditing : true);
      };
      editableItemModel.setIsMounted(true);
      return () => {
        // Unregister ourselves from the previous editable item model
        editableItemModelRef.current = undefined;
        editableItemModel.setIsMounted(false);
      };
    } else {
      setIsManagerEditing(true);
      return undefined;
    }
  },
  // eslint-disable-next-line abinitio/exhaustive-deps
  [editManager]);
  const isInEditMode = editManager ? editManager.currentlyEditing : isManagerEditing;
  return {
    isEditing: props.isEditable && isInEditMode,
    isInEditMode: isInEditMode,
    setItemWasChanged: changed => {
      editableItemModelRef.current?.setItemWasChanged(changed);
    },
    setDynamicValidationIssues: () => {
      const issues = editableItemModelRef.current?.validationIssues();
      if (issues != null) {
        editableItemModelRef.current?.setDynamicValidationIssues(issues);
      }
    }
  };
}
export function useSingleValueEditableItemViewModel(props) {
  const forceUpdate = useForceUpdate();
  const subject = props.subject;
  const editableItemViewModel = useEditableItemViewModel({
    createEditableItem: props.createEditableItem,
    editManager: props.editManager,
    isEditable: props.isEditable
  });
  return {
    ...editableItemViewModel,
    changeValue: newValue => {
      if (!subject) {
        throw new Error(`Unexpectedly trying to change value for undefined subject in AbFormHooks.useSingleValueEditableItemViewModel`);
      }
      if (!editableItemViewModel.setItemWasChanged) {
        throw new Error(`Unable to set item changed in AbFormHooks.useSingleValueEditableItemViewModel`);
      }
      const newSubject = props.changeValue(subject, newValue ?? null);
      const isModified = newSubject !== props.subject || props.isModified(newSubject);
      editableItemViewModel.setItemWasChanged(isModified);

      // This should be unnecessary, but we think that this might fix ABSDK-4079
      // Theory:
      // - The value is being set in the unsaved value store, but the mobx-driven force 
      //   update doesn't happen within this onChange (under poorly understood conditions)
      // - This force update ensures that we will always get called during the onChange
      forceUpdate();
      return newSubject;
    },
    revertChange: subject && props.isEditable && editableItemViewModel.isInEditMode && props.revertChange ? () => {
      if (!editableItemViewModel.setItemWasChanged) {
        throw new Error(`Unable to set item changed in AbFormHooks.useSingleValueEditableItemViewModel`);
      }
      if (props.revertChange) {
        props.revertChange(subject);
        editableItemViewModel.setItemWasChanged(false);
      }
    } : undefined
  };
}
export function useDataValueInputViewModel(componentName, props) {
  const editableItemContext = useSingleValueEditableItemViewModel(DataValueFieldFactory.createEditableItemContext(props));
  useOnMount(() => {
    if (props.publishDynamicValidationIssues) {
      editableItemContext.setDynamicValidationIssues();
    }
  });
  const changeValue = React.useCallback(value => {
    editableItemContext.changeValue(value);
    if (props.publishDynamicValidationIssues) {
      editableItemContext.setDynamicValidationIssues();
    }
  }, [editableItemContext, props.publishDynamicValidationIssues]);
  return MobXReactUtils.useForceUpdateWhenObservedObservableChanges(() => {
    const dataValueInputPropsVal = dataValueInputProps(props, editableItemContext);
    if (!dataValueInputPropsVal || !props.subject) {
      return null;
    }
    const currentValue = DataValueFieldFactory.getCurrentValue(props.subject, props.accessor, props.currentValue);
    return {
      subject: props.subject,
      accessor: props.accessor,
      inputProps: {
        ...dataValueInputPropsVal,
        ...{
          ...ReactUtils.inputProps(componentName, props)
        },
        dataTestId: props.dataTestId
      },
      readOnlyProps: {
        ...ReactUtils.componentProps(componentName, props, ["ab-is-read-only"]),
        dataTestId: props.dataTestId
      },
      isEditing: editableItemContext.isEditing,
      isInEditMode: editableItemContext.isInEditMode,
      currentValue: currentValue,
      changeValue: changeValue,
      onChange: props.onChange,
      onRevert: editableItemContext.revertChange
    };
  });
}
export function useDataValueFieldViewModel(componentName, props, decoratorsFunction) {
  const editableItemContext = useSingleValueEditableItemViewModel(DataValueFieldFactory.createEditableItemContext(props));
  return MobXReactUtils.useForceUpdateWhenObservedObservableChanges(() => {
    const {
      id,
      className,
      style,
      dataTestId,
      label,
      ...rawInputProps
    } = props;
    const inputProps = dataValueInputProps(rawInputProps, editableItemContext);
    if (!inputProps || !props.subject) {
      return null;
    }
    const currentValue = DataValueFieldFactory.getCurrentValue(props.subject, props.accessor, props.currentValue);
    if (props.hideIfUnset && !editableItemContext.isEditing && (currentValue === null || Array.isArray(currentValue) && currentValue.length === 0)) {
      return null;
    }
    const validationIssues = DataValueFieldFactory.performValidation({
      ...props,
      isInEditMode: editableItemContext.isInEditMode
    });
    const internalValidationError = validationIssues.find(validationIssue => validationIssue.internalError !== undefined)?.internalError;
    const fieldProps = {
      ...props,
      ...DataValueFieldFactory.fieldProps(componentName, editableItemContext.isEditing, editableItemContext.isInEditMode, validationIssues, props, decoratorsFunction),
      onChange: inputProps.onChange,
      onRevert: inputProps.onRevert,
      renderError: internalValidationError,
      renderErrorMessage: internalValidationError ? AbReactResources.field_unexpectedValidationError() : undefined
    };
    const {
      changeValue,
      revertChange
    } = editableItemContext;
    return {
      subject: props.subject,
      accessor: props.accessor,
      fieldProps: fieldProps,
      readOnlyProps: {
        ...ReactUtils.componentProps(componentName, props, ["ab-is-read-only"]),
        dataTestId: props.dataTestId
      },
      inputProps: {
        ...inputProps,
        id: id ? `${id}-input` : undefined
      },
      isEditing: editableItemContext.isEditing,
      isInEditMode: editableItemContext.isInEditMode,
      validationIssues: validationIssues,
      currentValue: currentValue,
      changeValue: changeValue,
      onChange: inputProps.onChange,
      revertChange: revertChange
    };
  });
}
export function dataValueInputProps(props, editableItemContext) {
  const {
    revertChange
  } = editableItemContext;
  return {
    ...props,
    currentValue: props.currentValue,
    onRevert: revertChange ? (originalSubject, originalValue) => {
      revertChange(originalValue);
      return originalSubject;
    } : undefined
  };
}
//# sourceMappingURL=AbFormHooks.js.map
